import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatMXN } from '@/components/ContractConceptsList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SabanaPage = () => {
  const navigate = useNavigate();
  const { selectedProject } = useApp();
  const [sabanaData, setSabanaData] = useState([]);
  const [weekColumns, setWeekColumns] = useState([]);
  const [totalWeeks, setTotalWeeks] = useState(3);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [totals, setTotals] = useState(null); // { importe_contratado, importe_ejecutado, avance_importe }
  const [loading, setLoading] = useState(true);

  const fetchSabanaData = useCallback(async () => {
    if (!selectedProject) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/sabana`, {
        params: { project_id: selectedProject.id }
      });
      const result = response.data;
      
      // Handle new response format with data and metadata
      if (result && result.data && Array.isArray(result.data)) {
        setSabanaData(result.data);
        setTotalWeeks(result.total_weeks || 3);
        setCurrentWeek(result.current_week || 1);
        setTotals(result.totals || null);
        
        // Build week columns array
        const columns = [];
        for (let i = 1; i <= Math.min(result.total_weeks || 3, 10); i++) {
          // Get week info from first row if available
          const firstRow = result.data[0];
          const weekInfo = firstRow?.weeks?.[`sem_${i}`] || {};
          columns.push({
            number: i,
            name: `SEM.${i}`,
            dateRange: weekInfo.date_range || '',
            status: weekInfo.status || 'open'
          });
        }
        setWeekColumns(columns);
      } else if (Array.isArray(result)) {
        // Old format fallback
        setSabanaData(result);
        setWeekColumns([
          { number: 1, name: 'SEM.1', dateRange: '', status: 'open' },
          { number: 2, name: 'SEM.2', dateRange: '', status: 'open' },
          { number: 3, name: 'SEM.3', dateRange: '', status: 'open' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching sabana data:', error);
      toast.error('Error al cargar datos de la Sábana');
      setSabanaData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) {
      navigate('/contratos');
      return;
    }
    fetchSabanaData();
  }, [selectedProject, navigate, fetchSabanaData]);

  const handleBack = () => {
    navigate('/contrato');
  };

  const getWeekTotal = (row, weekNum) => {
    // Try to get from weeks object first (new format)
    if (row.weeks && row.weeks[`sem_${weekNum}`]) {
      return row.weeks[`sem_${weekNum}`].total || 0;
    }
    // Fallback to flattened sem_X property
    return row[`sem_${weekNum}`] || 0;
  };

  if (!selectedProject) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#060C18]" data-testid="sabana-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="text-[#E2EAF4] hover:bg-[#1E3550]"
              data-testid="back-to-contrato"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-[#22C55E]">LA SÁBANA</h1>
              <p className="text-[#5A7A9A] text-sm">{selectedProject.name}</p>
              <p className="text-xs text-[#5A7A9A] mt-1">
                Semana actual: <span className="text-[#22C55E]">SEM.{currentWeek}</span>
                {' · '}{totalWeeks} semanas totales
              </p>
            </div>
            {totals && (
              <div className="hidden sm:block text-right mr-2" data-testid="sabana-importe-summary">
                <p className="text-[10px] uppercase tracking-wide text-[#5A7A9A]">Importe ejecutado</p>
                <p className="font-mono font-bold text-[#00CFFF]" data-testid="sabana-total-ejecutado">{formatMXN(totals.importe_ejecutado)}</p>
                <p className="text-xs text-[#5A7A9A]">
                  de <span className="font-mono" data-testid="sabana-total-contratado">{formatMXN(totals.importe_contratado)}</span> · {totals.avance_importe.toFixed(1)}%
                </p>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchSabanaData}
              className="text-[#E2EAF4] hover:bg-[#1E3550]"
              disabled={loading}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#0066FF]" />
          </div>
        ) : (
          <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-auto">
            <table className="w-full text-sm" data-testid="sabana-table">
              <thead>
                <tr className="bg-[#0A1422] border-b border-[#1E3550]">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-[#5A7A9A] uppercase sticky left-0 bg-[#0A1422] z-10">CLAVE</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-[#5A7A9A] uppercase min-w-[200px]">DESCRIPCIÓN</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-[#5A7A9A] uppercase">UNIDAD</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#5A7A9A] uppercase">CONTRATADO</th>
                  
                  {/* Dynamic week columns */}
                  {weekColumns.map(week => (
                    <th 
                      key={week.number}
                      className={`px-3 py-3 text-right text-xs font-semibold uppercase ${
                        week.number === currentWeek ? 'text-[#FF6B00]' : 'text-[#5A7A9A]'
                      }`}
                      title={week.dateRange}
                    >
                      <div className="flex flex-col items-end">
                        <span>{week.name}</span>
                        {week.status === 'closed' && (
                          <span className="text-[10px] text-[#22C55E]">CERRADA</span>
                        )}
                        {week.number === currentWeek && week.status !== 'closed' && (
                          <span className="text-[10px] text-[#FF6B00]">ACTUAL</span>
                        )}
                      </div>
                    </th>
                  ))}
                  
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#22C55E] uppercase">ACUMULADO</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#5A7A9A] uppercase">SALDO</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-[#5A7A9A] uppercase">AVANCE %</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#5A7A9A] uppercase">PU</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#5A7A9A] uppercase">IMPORTE</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-[#00CFFF] uppercase">IMP. EJECUTADO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E3550]">
                {sabanaData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-[#0D1520]' : 'bg-[#0A1422]'} data-testid={`sabana-row-${row.clave}`}>
                    <td className="px-3 py-3 font-mono font-semibold text-[#00CFFF] sticky left-0 bg-inherit">{row.clave}</td>
                    <td className="px-3 py-3 text-left text-[#E2EAF4]">{row.descripcion}</td>
                    <td className="px-3 py-3 text-center font-mono text-[#5A7A9A]">{row.unidad}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#E2EAF4]">{row.contratado?.toFixed(2) || '0.00'}</td>
                    
                    {/* Dynamic week values */}
                    {weekColumns.map(week => {
                      const value = getWeekTotal(row, week.number);
                      return (
                        <td 
                          key={week.number}
                          className={`px-3 py-3 text-right font-mono ${
                            week.number === currentWeek ? 'text-[#FF6B00] font-semibold' : 'text-[#5A7A9A]'
                          }`}
                        >
                          {value.toFixed(2)}
                        </td>
                      );
                    })}
                    
                    <td className="px-3 py-3 text-right font-mono font-semibold text-[#22C55E]">{row.acumulado?.toFixed(2) || '0.00'}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#E2EAF4]">{row.saldo?.toFixed(2) || '0.00'}</td>
                    <td className="px-3 py-3 text-center font-mono font-bold">
                      <span className={row.avance > 100 ? 'text-[#FF6B00]' : row.avance >= 100 ? 'text-[#22C55E]' : 'text-[#0066FF]'}>
                        {row.avance?.toFixed(1) || '0.0'}%
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-[#5A7A9A] whitespace-nowrap">{formatMXN(row.precio_unitario)}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#E2EAF4] whitespace-nowrap" data-testid={`sabana-importe-${row.clave}-${index}`}>{formatMXN(row.importe)}</td>
                    <td className="px-3 py-3 text-right font-mono font-semibold text-[#00CFFF] whitespace-nowrap" data-testid={`sabana-ejecutado-${row.clave}-${index}`}>{formatMXN(row.importe_ejecutado)}</td>
                  </tr>
                ))}
              </tbody>
              {totals && sabanaData.length > 0 && (
                <tfoot>
                  <tr className="bg-[#0A1422] border-t-2 border-[#1E3550] font-semibold" data-testid="sabana-totals-row">
                    <td className="px-3 py-3 text-[#E2EAF4] sticky left-0 bg-[#0A1422]" colSpan={4 + weekColumns.length + 3}>
                      TOTAL CONTRATO (MXN)
                      <span className="ml-2 text-xs font-normal text-[#5A7A9A]">{totals.avance_importe.toFixed(1)}% ejecutado</span>
                    </td>
                    <td></td>
                    <td className="px-3 py-3 text-right font-mono text-[#E2EAF4] whitespace-nowrap">{formatMXN(totals.importe_contratado)}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#00CFFF] whitespace-nowrap">{formatMXN(totals.importe_ejecutado)}</td>
                  </tr>
                </tfoot>
              )}
            </table>

            {sabanaData.length === 0 && (
              <div className="p-8 text-center text-[#5A7A9A]">
                No hay datos disponibles
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SabanaPage;
